import React, { useState, useEffect, useMemo, useCallback, Suspense } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Map, 
  Store, 
  TrendingUp, 
  Star, 
  Megaphone,
  FileText,
  Shield,
  ShieldAlert,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  LogOut,
  User,
  Users,
  FileCheck,
  Newspaper,
  Scale,
  Settings,
  Bell,
  Smartphone,
  MapPin,
  Building2,
  Mail,
  Image,
  Layers,
  Flag,
  GalleryHorizontalEnd,
  Globe,
  Heart,
  BarChart3,
  MessageSquare,
  Cookie,
  Plug,
  ClipboardList,
  KeyRound,
  Cpu,
  Inbox,
  Ticket,
  Award,
  Database,
  HardDrive,
  Camera,
  ShieldCheck,
  FileSignature,
  FileWarning,
  GraduationCap,
  BookOpen,
  Link2,
  Handshake,
  HelpCircle,
  Wallet,
  Calendar,
  Package,
  CreditCard,
  Sparkles,
  DatabaseBackup,
  Wrench,
  Search,
} from 'lucide-react';
import { useAdmin, PERMISSIONS } from '../context/AdminContext';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Badge } from '../../components/ui/badge';
import AdminMunicipioSelector, { AdminTenantProvider, useAdminTenant } from './AdminMunicipioSelector';
import AdminAlertBell from './AdminAlertBell';
import { api } from '../../services/api';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useAdminBrandLogo, useModerationPending } from './useAdminLayoutHooks';
import BackendImage from '../../components/BackendImage';

// Sidebar organizado em 9 grupos temáticos (accordion single-open).
// `key` identifica o grupo pra persistência em localStorage e permite mudar
// o rótulo sem quebrar o estado salvo.
const menuItems = [
  {
    key: 'paineis',
    section: 'Painéis',
    icon: LayoutDashboard,
    items: [
      { path: '/admin/visao-geral', icon: LayoutDashboard, label: 'Visão Geral', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/relatorios', icon: BarChart3, label: 'Relatórios', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/relatorios/agendamentos', icon: Calendar, label: 'Agendamento de Relatórios', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/painel-social', icon: Heart, label: 'Painel Social', permission: PERMISSIONS.VIEW_RELATORIOS },
      { path: '/admin/seo', icon: Globe, label: 'Painel SEO', permission: PERMISSIONS.VIEW_DASHBOARD, adminMasterOnly: true },
      { path: '/admin/dpo', icon: ShieldCheck, label: 'Painel DPO', permission: PERMISSIONS.VIEW_DASHBOARD, dpoOnly: true },
      { path: '/admin/dashboard-territorial', icon: MapPin, label: 'Indicadores Territoriais', permission: PERMISSIONS.VIEW_RELATORIOS },
      { path: '/admin/mapa-territorio', icon: Map, label: 'Mapa do Território', permission: PERMISSIONS.VIEW_DASHBOARD },
    ],
  },
  {
    key: 'operacao',
    section: 'Operação',
    icon: FileCheck,
    items: [
      { path: '/admin/moderacao', icon: FileCheck, label: 'Fila de Moderação', permission: PERMISSIONS.VIEW_NEGOCIOS, badge: 'mod_pending' },
      { path: '/admin/moderacao-conteudo', icon: Shield, label: 'Moderação de Conteúdo', permission: PERMISSIONS.VIEW_NEGOCIOS },
      { path: '/admin/denuncias', icon: Flag, label: 'Denúncias', permission: PERMISSIONS.VIEW_NEGOCIOS },
      { path: '/admin/negocios/aceites-pendentes', icon: ShieldCheck, label: 'Aceites Pendentes', permission: PERMISSIONS.VIEW_NEGOCIOS },
      { path: '/admin/palavras-proibidas', icon: ShieldAlert, label: 'Palavras Proibidas', permission: PERMISSIONS.VIEW_NEGOCIOS },
      { path: '/admin/configuracoes-ia', icon: Cpu, label: 'Config. de IA', permission: PERMISSIONS.VIEW_DASHBOARD, adminMasterOnly: true },
      { path: '/admin/dados-mock', icon: Database, label: 'Dados Mock (IA)', permission: PERMISSIONS.VIEW_DASHBOARD, adminMasterOnly: true },
      { path: '/admin/diagnostico-visibilidade', icon: Search, label: 'Diagnóstico Explorar', permission: PERMISSIONS.VIEW_NEGOCIOS },
    ],
  },
  {
    key: 'cadastros',
    section: 'Cadastros',
    icon: Users,
    items: [
      { path: '/admin/empreendedores', icon: Users, label: 'Empreendedores', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/consumidores', icon: Users, label: 'Consumidores', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/gestores', icon: Shield, label: 'Gestores', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/negocios', icon: Store, label: 'Negócios', permission: PERMISSIONS.VIEW_NEGOCIOS },
      { path: '/admin/coletivos', icon: Handshake, label: 'Coletivos', permission: PERMISSIONS.VIEW_COLETIVOS },
      { path: '/admin/organizacoes-sociais', icon: Heart, label: 'Organizações Sociais', permission: PERMISSIONS.VIEW_ASSOCIACOES },
      { path: '/admin/vitrines', icon: Store, label: 'Vitrines Temáticas', permission: PERMISSIONS.VIEW_NEGOCIOS },
      { path: '/admin/programas-acoes', icon: Megaphone, label: 'Eventos, Programas e Ações', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/solicitacoes', icon: HelpCircle, label: 'Solicitações', permission: PERMISSIONS.VIEW_NEGOCIOS },
    ],
  },
  {
    key: 'analise',
    section: 'Análise',
    icon: TrendingUp,
    items: [
      { path: '/admin/gamificacao', icon: Award, label: 'Jornada do Negócio', permission: PERMISSIONS.VIEW_RELATORIOS },
      { path: '/admin/demanda', icon: TrendingUp, label: 'Demanda', permission: PERMISSIONS.VIEW_RELATORIOS },
      { path: '/admin/qualidade', icon: Star, label: 'Qualidade', permission: PERMISSIONS.VIEW_RELATORIOS },
      { path: '/admin/painel-negocios', icon: BarChart3, label: 'Indicadores Econômicos', permission: PERMISSIONS.VIEW_RELATORIOS },
      { path: '/admin/painel-empreendedores', icon: Users, label: 'Perfil Empreendedores', permission: PERMISSIONS.VIEW_RELATORIOS },
      { path: '/admin/recomendacoes', icon: Link2, label: 'Recomendações', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/relatorios-detalhados', icon: FileText, label: 'Relatórios Detalhados', permission: PERMISSIONS.VIEW_RELATORIOS },
    ],
  },
  {
    key: 'marketing',
    section: 'Marketing',
    icon: Megaphone,
    items: [
      { path: '/admin/cupons', icon: Ticket, label: 'Cupons e Descontos', permission: PERMISSIONS.VIEW_NEGOCIOS },
      { path: '/admin/campanhas', icon: Megaphone, label: 'Campanhas', permission: PERMISSIONS.VIEW_NEGOCIOS },
      { path: '/admin/alertas', icon: Bell, label: 'Central de Alertas', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/defesa-civil', icon: ShieldAlert, label: 'Defesa Civil', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/pwa-stats', icon: Smartphone, label: 'PWA & Engajamento', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/conteudo/noticias', icon: Newspaper, label: 'Notícias', permission: PERMISSIONS.VIEW_CMS },
      { path: '/admin/hero-banners', icon: GalleryHorizontalEnd, label: 'Banner Principal', permission: PERMISSIONS.VIEW_CMS },
      { path: '/admin/banners', icon: Image, label: 'Banners', permission: PERMISSIONS.VIEW_DASHBOARD },
    ],
  },
  {
    key: 'comunicacao',
    section: 'Comunicação',
    icon: Mail,
    items: [
      { path: '/admin/email-templates', icon: FileText, label: 'Templates de Email', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/email-logs', icon: Inbox, label: 'Histórico de Emails', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/email-config', icon: Mail, label: 'Config. Email', permission: PERMISSIONS.VIEW_DASHBOARD, adminMasterOnly: true },
      { path: '/admin/whatsapp-template', icon: MessageSquare, label: 'Template WhatsApp', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/sms', icon: MessageSquare, label: 'SMS', permission: PERMISSIONS.VIEW_DASHBOARD, adminMasterOnly: true },
      { path: '/admin/custos-comunicacao', icon: Wallet, label: 'Custos de Comunicação', permission: PERMISSIONS.VIEW_DASHBOARD, adminMasterOnly: true },
      { path: '/admin/faq', icon: HelpCircle, label: 'FAQ (público)', permission: PERMISSIONS.VIEW_CMS },
      { path: '/admin/fale-conosco', icon: MessageSquare, label: 'Fale Conosco', permission: PERMISSIONS.VIEW_CMS },
    ],
  },
  {
    key: 'territorio',
    section: 'Território',
    icon: Globe,
    items: [
      { path: '/admin/municipios', icon: Globe, label: 'Municípios', permission: PERMISSIONS.VIEW_DASHBOARD, adminMasterOnly: true },
      { path: '/admin/territorios', icon: MapPin, label: 'Territórios', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/geocode-config', icon: MapPin, label: 'Raio de Cobertura', permission: PERMISSIONS.VIEW_DASHBOARD, adminMasterOnly: true },
      { path: '/admin/categorias', icon: Layers, label: 'Categorias', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/curadoria-tags', icon: Sparkles, label: 'Curadoria de Tags', permission: PERMISSIONS.VIEW_DASHBOARD, adminMasterOnly: true },
      { path: '/admin/parceiros', icon: Building2, label: 'Parceiros', permission: PERMISSIONS.VIEW_DASHBOARD },
    ],
  },
  {
    key: 'compliance',
    section: 'Compliance & LGPD',
    icon: ShieldCheck,
    items: [
      { path: '/admin/dpo/nomeacao', icon: FileSignature, label: 'Nomeação e Documentos', permission: PERMISSIONS.VIEW_DASHBOARD, dpoOnly: true },
      { path: '/admin/dpo/titulares', icon: Inbox, label: 'Solicitações de Titulares', permission: PERMISSIONS.VIEW_DASHBOARD, dpoOnly: true },
      { path: '/admin/dpo/incidentes', icon: FileWarning, label: 'Incidentes de Segurança', permission: PERMISSIONS.VIEW_DASHBOARD, dpoOnly: true },
      { path: '/admin/dpo/mapeamento', icon: ClipboardList, label: 'Mapeamento (RIPD)', permission: PERMISSIONS.VIEW_DASHBOARD, dpoOnly: true },
      { path: '/admin/dpo/subprocessadores', icon: Building2, label: 'Sub-processadores', permission: PERMISSIONS.VIEW_DASHBOARD, dpoOnly: true },
      { path: '/admin/dpo/treinamentos', icon: GraduationCap, label: 'Treinamentos', permission: PERMISSIONS.VIEW_DASHBOARD, dpoOnly: true },
      { path: '/admin/dpo/templates', icon: BookOpen, label: 'Templates DPO', permission: PERMISSIONS.VIEW_DASHBOARD, dpoOnly: true },
      { path: '/admin/dpo/relatorios', icon: BarChart3, label: 'Relatórios e Evidências', permission: PERMISSIONS.VIEW_DASHBOARD, dpoOnly: true },
      { path: '/admin/dpo/export', icon: Database, label: 'Exportar Dados (LGPD)', permission: PERMISSIONS.VIEW_DASHBOARD, dpoOnly: true },
      { path: '/admin/governanca-lgpd', icon: ClipboardList, label: 'Auditoria', permission: PERMISSIONS.VIEW_AUDITORIA },
      { path: '/admin/alertas-sistema', icon: ShieldAlert, label: 'Alertas do Sistema', permission: PERMISSIONS.VIEW_AUDITORIA },
    ],
  },
  {
    key: 'delivery',
    section: 'Empreende Delivery',
    icon: Package,
    items: [
      { path: '/admin/delivery', icon: Package, label: 'Empreende Delivery', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/delivery/assinaturas', icon: CreditCard, label: 'Delivery — Assinaturas', permission: PERMISSIONS.VIEW_DASHBOARD, adminMasterOnly: true },
      { path: '/admin/delivery/municipios', icon: Package, label: 'Delivery — Municípios', permission: PERMISSIONS.VIEW_DASHBOARD, adminMasterOnly: true },
    ],
  },
  {
    key: 'configuracoes',
    section: 'Configurações',
    icon: Settings,
    items: [
      { path: '/admin/configuracoes', icon: Settings, label: 'Config. do Site', permission: PERMISSIONS.VIEW_DASHBOARD, adminMasterOnly: true },
      { path: '/admin/integracoes', icon: Plug, label: 'Integrações', permission: PERMISSIONS.VIEW_DASHBOARD, adminMasterOnly: true },
      { path: '/admin/exportar-banco', icon: DatabaseBackup, label: 'Backup & Restauração', permission: PERMISSIONS.VIEW_DASHBOARD, adminMasterOnly: true },
      { path: '/admin/armazenamento', icon: HardDrive, label: 'Armazenamento', permission: PERMISSIONS.VIEW_DASHBOARD, adminMasterOnly: true },
      { path: '/admin/limites-imagens', icon: Camera, label: 'Limites de imagens', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/permissoes', icon: Shield, label: 'Permissões (RBAC)', permission: PERMISSIONS.VIEW_AUDITORIA, adminMasterOnly: true },
      { path: '/admin/verificacao-usuarios', icon: ShieldCheck, label: 'Verificação de Usuários', permission: PERMISSIONS.VIEW_AUDITORIA },
      { path: '/admin/feature-flags', icon: Flag, label: 'Feature Flags', permission: PERMISSIONS.VIEW_AUDITORIA, adminMasterOnly: true },
      { path: '/admin/manutencao', icon: Wrench, label: 'Manutenção (Busca)', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/seguranca', icon: KeyRound, label: 'Segurança (2FA)', permission: PERMISSIONS.VIEW_DASHBOARD },
      { path: '/admin/conteudo/lgpd', icon: Shield, label: 'LGPD (página pública)', permission: PERMISSIONS.VIEW_CMS },
      { path: '/admin/conteudo/termos-de-uso', icon: Scale, label: 'Termos de Uso', permission: PERMISSIONS.VIEW_CMS },
      { path: '/admin/conteudo/cookies', icon: Cookie, label: 'Cookies', permission: PERMISSIONS.VIEW_CMS },
      { path: '/admin/ajuda', icon: BookOpen, label: 'Ajuda / FAQ interno', permission: PERMISSIONS.VIEW_DASHBOARD, alwaysVisible: true },
    ],
  },
];

const getRoleLabel = (role) => {
  const labels = {
    'adm_master': 'Admin Master',
    'dpo': 'DPO',
    'gestor': 'Gestor',
    'tecnico': 'Técnico',
    'analista': 'Analista',
    'auditor': 'Auditor'
  };
  return labels[role] || role;
};

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { adminUser, hasPermission, ROLES } = useAdmin();
  const { logout: authLogout } = useAuth();
  const { isPathEnabled } = useFeatureFlags();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const modPending = useModerationPending(location.pathname);
  const brandLogo = useAdminBrandLogo(location.pathname);

  // Encontra qual grupo do sidebar contém a rota atual — usado pra abrir
  // exatamente esse grupo no accordion quando o usuário chega numa página.
  const groupOfActiveRoute = useMemo(() => {
    for (const group of menuItems) {
      if (group.items.some((it) => location.pathname === it.path || location.pathname.startsWith(it.path + '/'))) {
        return group.key;
      }
    }
    return menuItems[0].key;
  }, [location.pathname]);

  // Accordion single-open: um grupo por vez. Persiste em localStorage pra
  // manter a escolha do gestor entre sessões (ex: quem trabalha só com DPO
  // não precisa reabrir Compliance toda vez).
  const [expandedGroup, setExpandedGroup] = useState(() => {
    try {
      const saved = localStorage.getItem('admin_sidebar_expanded_group');
      if (saved && menuItems.some((g) => g.key === saved)) return saved;
    } catch (_) { /* ignore storage errors */ }
    return groupOfActiveRoute;
  });

  // Ao navegar entre rotas, se o grupo da rota atual não estiver aberto,
  // troca automaticamente pra ele — evita usuário "perdido" com o menu
  // apontando pra um grupo que não contém a página atual.
  useEffect(() => {
    if (groupOfActiveRoute && groupOfActiveRoute !== expandedGroup) {
      setExpandedGroup(groupOfActiveRoute);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupOfActiveRoute]);

  const toggleGroup = useCallback((key) => {
    setExpandedGroup((prev) => {
      const next = prev === key ? null : key;
      try { localStorage.setItem('admin_sidebar_expanded_group', next || ''); } catch (_) { /* ignore */ }
      return next;
    });
  }, []);

  const handleLogout = () => {
    // Limpa toda a sessão (admin + empreendedor) para evitar identidade mista.
    try { authLogout(); } catch { /* defensive */ }
    navigate('/admin/login');
  };

  const getBadgeCount = (badgeType) => {
    if (badgeType === 'mod_pending') return modPending;
    return 0;
  };

  const isActive = (path) => {
    if (path === '/admin/moderacao') {
      // Match only the exact /admin/moderacao route (or sub-routes with trailing slash),
      // not sibling routes like /admin/moderacao-conteudo.
      return location.pathname === '/admin/moderacao' || location.pathname.startsWith('/admin/moderacao/');
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="min-h-screen bg-[#F7F7FB]">
      {/* Top Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-[#56007F] text-white z-50 flex items-center justify-between px-4 shadow-lg">
        <div className="flex items-center gap-4">
          {/* Mobile menu toggle */}
          <button
            className="lg:hidden p-2 hover:bg-white/10 rounded-lg"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          
          {/* Desktop sidebar toggle */}
          <button
            className="hidden lg:flex p-2 hover:bg-white/10 rounded-lg"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </button>
          
          <Link to="/admin/visao-geral" className="flex items-center gap-3">
            {brandLogo ? (
              <BackendImage
                src={brandLogo}
                alt="Empreende Comunidade"
                className="h-10 w-auto object-contain"
                data-testid="admin-brand-logo"
                eager
                fallback={<div className="h-10 w-32" aria-hidden="true" />}
              />
            ) : (
              <div className="h-10 w-32" aria-hidden="true" />
            )}
            <span className="font-bold text-lg hidden sm:inline" style={{ fontFamily: 'Manrope' }}>
              Admin
            </span>
          </Link>
        </div>

        <div className="flex items-center gap-3">
          {/* Municipality Selector (Admin Master only) */}
          <AdminMunicipioSelector 
            isAdminMaster={adminUser?.role === 'adm_master'}
            userMunicipio={adminUser?.municipio_id ? { id: adminUser.municipio_id, nome: 'Meu Município' } : null}
          />
          
          {/* System Alert Bell */}
          <AdminAlertBell />

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 p-2 hover:bg-white/10 rounded-lg">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4" />
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium">{adminUser?.nome || 'Admin'}</p>
                  <p className="text-xs text-white/70">{adminUser?.role ? getRoleLabel(adminUser.role) : ''}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Minha Conta</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled>
                <Badge variant="secondary" className="mr-2">{adminUser?.role ? getRoleLabel(adminUser.role) : '—'}</Badge>
              </DropdownMenuItem>
              <DropdownMenuItem disabled className="text-xs">
                {adminUser?.email || ''}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Sair
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/')}>
                Voltar ao Site
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-16 left-0 bottom-0 bg-white border-r border-[#E6E6EF] z-40 transition-all duration-300 overflow-y-auto
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${sidebarOpen ? 'w-64' : 'w-20'}
        `}
      >
        <nav className="p-4 space-y-2">
          {menuItems.map((section) => {
            // Filtra itens visíveis para calcular se o grupo aparece
            const visibleItems = section.items.filter((item) => {
              if (!hasPermission(item.permission)) return false;
              if (item.adminMasterOnly && adminUser?.role !== 'adm_master') return false;
              if (item.dpoOnly && !['adm_master', 'dpo'].includes(adminUser?.role)) return false;
              if (!isPathEnabled(item.path)) return false;
              return true;
            });
            if (visibleItems.length === 0) return null;

            const isExpanded = sidebarOpen && expandedGroup === section.key;
            const groupHasActive = section.items.some((it) => isActive(it.path));
            const SectionIcon = section.icon;

            // Sidebar colapsado (só ícones): mostra todos os itens sem accordion.
            if (!sidebarOpen) {
              return (
                <div key={section.key} className="space-y-1">
                  {visibleItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    const badgeCount = item.badge ? getBadgeCount(item.badge) : 0;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors relative
                          ${active
                            ? 'bg-[#56007F]/10 text-[#56007F] font-medium'
                            : 'text-[#667085] hover:bg-gray-100 hover:text-[#121212]'
                          }
                        `}
                        title={item.label}
                      >
                        <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-[#56007F]' : ''}`} />
                        {badgeCount > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FFA52A] text-white text-xs font-bold rounded-full flex items-center justify-center">
                            {badgeCount}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              );
            }

            return (
              <div key={section.key} className="border-b border-gray-100 last:border-b-0 pb-1">
                <button
                  type="button"
                  onClick={() => toggleGroup(section.key)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-colors
                    ${groupHasActive ? 'text-[#56007F]' : 'text-[#667085] hover:bg-gray-50'}
                  `}
                  data-testid={`sidebar-group-${section.key}`}
                  aria-expanded={isExpanded}
                >
                  <SectionIcon className={`w-4 h-4 flex-shrink-0 ${groupHasActive ? 'text-[#56007F]' : ''}`} />
                  <span className="flex-1 text-left">{section.section}</span>
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  />
                </button>
                {isExpanded && (
                  <div className="mt-1 space-y-1 pl-2">
                    {visibleItems.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.path);
                      const badgeCount = item.badge ? getBadgeCount(item.badge) : 0;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm
                            ${active
                              ? 'bg-[#56007F]/10 text-[#56007F] font-medium'
                              : 'text-[#667085] hover:bg-gray-100 hover:text-[#121212]'
                            }
                          `}
                        >
                          <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-[#56007F]' : ''}`} />
                          <span className="flex-1">{item.label}</span>
                          {badgeCount > 0 && (
                            <Badge variant="destructive" className="bg-[#FFA52A] hover:bg-[#FFA52A]">
                              {badgeCount}
                            </Badge>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <main
        className={`pt-16 min-h-screen transition-all duration-300
          ${sidebarOpen ? 'lg:pl-64' : 'lg:pl-20'}
        `}
      >
        <div className="p-6">
          <Suspense fallback={
            <div className="space-y-4" data-testid="admin-page-skeleton">
              <div className="h-8 w-1/3 bg-gray-200 rounded animate-pulse" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="h-24 bg-gray-200 rounded animate-pulse" />
                <div className="h-24 bg-gray-200 rounded animate-pulse" />
                <div className="h-24 bg-gray-200 rounded animate-pulse" />
              </div>
              <div className="h-64 bg-gray-100 rounded animate-pulse" />
            </div>
          }>
            <Outlet />
          </Suspense>
        </div>
      </main>
    </div>
  );
}

// Wrapper with AdminTenantProvider
export function AdminLayoutWithProvider() {
  const { adminUser } = useAdmin();
  const isAdminMaster = adminUser?.role === 'adm_master';
  
  return (
    <AdminTenantProvider isAdminMaster={isAdminMaster} userMunicipioId={adminUser?.municipio_id}>
      <AdminLayout />
    </AdminTenantProvider>
  );
}

export { useAdminTenant } from './AdminMunicipioSelector';
